import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { TaskEditor } from "@/components/workflow/TaskPanel";
import {
  controlClass,
  errorText,
  Feedback,
  PeriodLockNotice,
  useResource,
  useWriteWindow,
} from "@/components/workflow/shared";
import { shiftDay } from "@/components/RosterAssignmentDialog";
import { useAuth } from "@/context/AuthContext";
import { useDanas } from "@/lib/dan";
import { datum } from "@/lib/format";
import type { Raspored } from "@/lib/gradilista";
import { crewWithoutTask, taskState } from "@/lib/siteCrewTasks";
import {
  outcomeLabels,
  workflow,
  type Crew,
  type SiteTask,
} from "@/lib/workflow";

const barStyles = {
  "U tijeku": "border-green-700 bg-green-100 text-green-950",
  Kasni: "border-orange-700 bg-orange-100 text-orange-950",
  Planirano: "border-dashed border-blue-700 bg-blue-50 text-blue-950",
  Završeno: "border-gray-500 bg-gray-200 text-gray-900",
  Otkazano: "border-gray-400 bg-gray-100 text-gray-700 line-through",
};
function monday(day: string) {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  return shiftDay(day, -(weekday === 0 ? 6 : weekday - 1));
}
function dayDistance(a: string, b: string) {
  return Math.round(
    (Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000,
  );
}

export function TaskTimeline({
  siteId,
  date,
  crews,
  tasks,
  roster,
  refresh,
}: {
  siteId: string;
  date: string;
  crews: Crew[];
  tasks: SiteTask[];
  roster: Raspored[];
  refresh: () => void;
}) {
  const { can } = useAuth();
  const today = useDanas();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<SiteTask | null>(null);
  const [editing, setEditing] = useState<SiteTask | "new" | null>(null);
  const [assignedCrew, setAssignedCrew] = useState("");
  const start = shiftDay(monday(date), offset);
  const end = shiftDay(start, 13);
  const days = Array.from({ length: 14 }, (_, i) => shiftDay(start, i));
  const active = crews.filter(
    (c) => c.aktivna || tasks.some((t) => t.brigada_id === c.id),
  );
  function bar(t: SiteTask, timeline: boolean) {
    const state = taskState(t, today);
    const left = Math.max(0, dayDistance(start, t.planirani_od));
    const right = Math.min(13, dayDistance(start, t.planirani_do));
    return (
      <button
        key={t.id}
        title={`${t.naziv} · ${state} · ${datum(t.planirani_od)} – ${datum(t.planirani_do)}`}
        className={`min-h-11 rounded-lg border-2 p-2 text-left ${barStyles[state]} ${timeline ? "w-full truncate" : "w-full"}`}
        style={
          timeline ? { gridColumn: `${left + 1} / ${right + 2}` } : undefined
        }
        onClick={() => setSelected(t)}
      >
        <strong>{t.naziv}</strong> · {state}
        <span className="block text-xs">
          {datum(t.planirani_od)} – {datum(t.planirani_do)}
        </span>
      </button>
    );
  }
  function newTask(crew = "") {
    setAssignedCrew(crew);
    setEditing("new");
  }
  return (
    <section className="space-y-4" aria-label="Taktovi gradilišta">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          className="min-h-11"
          variant="outline"
          onClick={() => setOffset((o) => o - 14)}
        >
          ← Prethodna 2 tjedna
        </Button>
        <strong>
          {datum(start)} – {datum(end)}
        </strong>
        <Button
          className="min-h-11"
          variant="outline"
          onClick={() => setOffset((o) => o + 14)}
        >
          Sljedeća 2 tjedna →
        </Button>
        {can("takt", "create") && (
          <Button className="min-h-11" onClick={() => newTask()}>
            + Novi takt
          </Button>
        )}
      </div>
      <p className="text-sm">
        Zeleno: u tijeku · narančasto: kasni · isprekidano: planirano · sivo:
        završeno. Crveni red: danas nema aktivnog takta.
      </p>
      <div className="hidden md:block" aria-hidden="true">
        <div className="ml-48 grid grid-cols-14">
          {days.map((d) => (
            <span key={d} className="text-center text-xs">
              {d.slice(8)}.{d.slice(5, 7)}.
            </span>
          ))}
        </div>
      </div>
      {active.map((crew) => {
        const missing = crew.aktivna && crewWithoutTask(crew, tasks, today);
        const visible = tasks.filter(
          (t) =>
            t.brigada_id === crew.id &&
            t.planirani_od <= end &&
            t.planirani_do >= start,
        );
        return (
          <article
            key={crew.id}
            data-testid={`crew-tasks-${crew.id}`}
            className={`rounded-xl border p-3 ${missing ? "border-red-500 bg-red-50 text-red-950" : "border-[var(--color-border)]"}`}
          >
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="md:w-44 md:shrink-0">
                <h2 className="font-bold">{crew.naziv}</h2>
                {missing && (
                  <>
                    <p className="text-sm">Danas bez aktivnog takta</p>
                    {can("takt", "create") && (
                      <Button
                        className="mt-2 min-h-11"
                        variant="outline"
                        onClick={() => newTask(crew.id)}
                      >
                        Dodijeli takt
                      </Button>
                    )}
                  </>
                )}
              </div>
              <div className="hidden min-w-0 flex-1 space-y-2 md:block">
                {visible.map((t) => (
                  <div key={t.id} className="grid grid-cols-14">
                    {bar(t, true)}
                  </div>
                ))}
                {!visible.length && <p>Nema taktova u ovom razdoblju.</p>}
              </div>
              <div className="space-y-2 md:hidden">
                {visible.map((t) => bar(t, false))}
                {!visible.length && <p>Nema taktova u ovom razdoblju.</p>}
              </div>
            </div>
          </article>
        );
      })}
      {!active.length && (
        <p>Još nema brigada. Najprije dodajte brigadu na kartici Brigade.</p>
      )}
      {selected && (
        <TaskDetail
          siteId={siteId}
          task={selected}
          crews={crews}
          roster={roster}
          onClose={() => setSelected(null)}
          onEdit={() => {
            setEditing(selected);
            setSelected(null);
          }}
          onChanged={() => {
            setSelected(null);
            refresh();
          }}
        />
      )}
      {editing && (
        <Dialog
          title={editing === "new" ? "Novi takt" : "Uredi takt"}
          onClose={() => setEditing(null)}
        >
          <TaskEditor
            siteId={siteId}
            task={editing === "new" ? null : editing}
            crews={crews}
            initialCrewId={editing === "new" ? assignedCrew : ""}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />
        </Dialog>
      )}
    </section>
  );
}

function TaskDetail({
  siteId,
  task,
  crews,
  roster,
  onClose,
  onEdit,
  onChanged,
}: {
  siteId: string;
  task: SiteTask;
  crews: Crew[];
  roster: Raspored[];
  onClose: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const today = useDanas();
  const history = useResource(
    task.id,
    useCallback((signal) => workflow.outcomes(task.id, signal), [task.id]),
  );
  const [extend, setExtend] = useState(false);
  const [until, setUntil] = useState(shiftDay(task.planirani_do, 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const window = useWriteWindow(
    siteId,
    task.planirani_od,
    extend ? until : task.planirani_do,
  );
  const crew = crews.find((c) => c.id === task.brigada_id);
  const leaders =
    crew?.clanovi.filter(
      (m) =>
        m.je_brigadir &&
        m.vrijedi_od <= task.planirani_do &&
        (!m.vrijedi_do || m.vrijedi_do >= task.planirani_od),
    ) ?? [];
  const names = [
    ...new Set(
      leaders.map(
        (m) =>
          roster.find((r) => r.radnik_id === m.radnik_id)?.radnik ??
          "Radnik više nije na rasporedu",
      ),
    ),
  ];
  const canUpdate = can("takt", "update") && task.status === "aktivan";
  async function update(finish: boolean) {
    if (busy || (finish ? window.periodBlocked : window.blocked)) return;
    setBusy(true);
    setError("");
    try {
      const { id, gradiliste_id, ...input } = task;
      void id;
      void gradiliste_id;
      await workflow.updateTask(task.id, {
        ...input,
        status: finish ? "zavrsen" : "aktivan",
        planirani_do: finish ? task.planirani_do : until,
      });
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={task.naziv}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <div className="space-y-4">
        <p>
          {crew?.naziv} · Brigadir: {names.join(", ") || "Bez brigadira"}
        </p>
        <p>
          {datum(task.planirani_od)} – {datum(task.planirani_do)} ·{" "}
          {taskState(task, today)}
        </p>
        {task.opis && <p>{task.opis}</p>}
        <Feedback error={error || history.error} />
        <h3 className="font-bold">Dnevni statusi</h3>
        {history.loading && <p role="status">Učitavanje dnevnih statusa…</p>}
        {history.data?.length === 0 && <p>Još nema dnevnih statusa.</p>}
        <ul className="space-y-2">
          {history.data?.map((d) => (
            <li key={d.id} className="rounded border p-2">
              {datum(d.datum)} · {outcomeLabels[d.status]} · Fotografija:{" "}
              {d.broj_fotografija}
              {d.komentar && (
                <p className="whitespace-pre-wrap">{d.komentar}</p>
              )}
            </li>
          ))}
        </ul>
        {canUpdate && (
          <>
            <PeriodLockNotice state={window.state} />
            <div className="flex flex-wrap gap-3">
              <Button
                className="min-h-11"
                variant="outline"
                disabled={busy}
                onClick={onEdit}
              >
                Uredi
              </Button>
              <Button
                className="min-h-11"
                variant="outline"
                disabled={busy}
                onClick={() => setExtend((x) => !x)}
              >
                Produlji do…
              </Button>
              <Button
                className="min-h-11"
                disabled={busy || window.periodBlocked}
                onClick={() => void update(true)}
              >
                Označi završenim
              </Button>
            </div>
            <p className="text-sm">
              Završetak je moguć nakon zaključivanja svih planiranih dana.
              Produljenje poštuje zaključane dane i postojeće dnevne dokaze.
            </p>
            {extend && (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void update(false);
                }}
              >
                <label className="block">
                  Novi planirani kraj
                  <input
                    className={controlClass}
                    type="date"
                    required
                    min={shiftDay(task.planirani_do, 1)}
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                </label>
                <Feedback error={window.error} />
                <Button
                  className="min-h-11"
                  disabled={
                    busy || window.blocked || until <= task.planirani_do
                  }
                >
                  Spremi produljenje
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
